/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import UiEmptyState from 'components/Ui/UiEmptyState'
import {CalculatorIcon, TrashIcon} from '@heroicons/react/24/outline'
import OfferCalculationItem from 'pages/Front/Offers/components/OfferCalculationItem'
import UiPill from 'components/Ui/UiPill'
import {getActiveOfferCalculations, getDeletedOfferCalculations, sortOfferCalculationsForDisplay} from 'utils/offers/offersUtils'
import {useTypedSelector} from 'helpers/reducers'


const OfferDetailCalculations = () => {
	const { t } = useTranslation()
	const selectedOffer = useTypedSelector((state) => state.offersState.selectedOffer)
	const [showDeleted, setShowDeleted] = React.useState(false)

	const calculations = selectedOffer.calculations ?? []

	const activeCalculations = React.useMemo(
		() => getActiveOfferCalculations(calculations),
		[calculations]
	)

	const deletedCalculations = React.useMemo(
		() => getDeletedOfferCalculations(calculations),
		[calculations]
	)

	const currentCalculations = showDeleted
		? deletedCalculations
		: activeCalculations

    const sortedCurrentCalculations = React.useMemo(
        () => sortOfferCalculationsForDisplay(currentCalculations),
        [currentCalculations]
    )

    return (
        <div className={clsx('OfferDetailCalculations col-span-8 flex flex-col UiCard overflow-hidden grow')}>
            <div className="flex flex-row border-b border-gray-200 dark:border-gray-700 shrink-0">
                <button
                    onClick={() => setShowDeleted(false)}
                    className={clsx(
                        'grow shrink-0 px-4 py-3 text-sm font-medium transition-colors border-r border-gray-100 dark:border-gray-800',
                        !showDeleted
                            ? 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-400'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    )}
                >
                    <div className="flex items-center justify-center gap-2">
                        <CalculatorIcon className="w-4 h-4" />
                        {t('Active Calculations')}
                        <UiPill className={'bg-gray-200/80'}>{activeCalculations.length}</UiPill>
                    </div>
                </button>
                <button
                    onClick={() => setShowDeleted(true)}
                    aria-label={`${t('Deleted Calculations')}: ${deletedCalculations.length}`}
                    title={t('Deleted Calculations')}
                    className={clsx(
                        'shrink-0 px-4 py-3 text-sm font-medium transition-colors',
                        showDeleted
                            ? 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 italic'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    )}
                >
                    <div className="flex items-center justify-center gap-2">
                        <TrashIcon className="w-4 h-4" />
                        <UiPill className={'bg-gray-200/80'}>{deletedCalculations.length}</UiPill>
                    </div>
                </button>
            </div>

            <div className="p-3 grow">
                <div
                    className={clsx(
                        'h-full grid gap-3',
                        (() => {
                            const count = currentCalculations.length
                            if (count === 1) return 'grid-cols-1'
                            return 'grid-cols-2'
                        })()
                    )}
                >
                    {currentCalculations.length === 0 && (
                        <div className="col-span-full">
                            <UiEmptyState
                                className="py-12"
                                icon={
                                    showDeleted ? (
                                        <TrashIcon className="w-12 h-12 text-gray-400 dark:text-gray-600" />
                                    ) : (
                                        <CalculatorIcon className="w-12 h-12 text-gray-400 dark:text-gray-600" />
                                    )
                                }
                                title={showDeleted ? t('No binned calculations') : t('No calculations')}
                            />
                        </div>
                    )}
                    {sortedCurrentCalculations.length > 0 &&
                        sortedCurrentCalculations.map((calc, index) => (
                            <OfferCalculationItem
                                calc={calc}
                                key={calc.id}
                                offer={selectedOffer}
                                index={index}
                            />
                        ))}
                </div>
            </div>
        </div>
    )
}

export default OfferDetailCalculations
